<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :tabs="tabs"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        title="Customer"
        :baseView="baseView"
        :primaryLabel="model.CustomerName"
        :secondaryLabel="model.CustomerCode"
        recordType="Customer"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 split-view">
      <gc-split-view
        :showLeftContainer="showLeftContainer"
        :showRightContainer="showRightContainer"
      >
        <template #leftContent>
          <div class="css-v2 split-view-list">
            <GcDataGrid
              :grouping="false"
              :export="true"
              :dataSourceConfig="dataSourceConfig"
              :columns="columns"
              :gridToolbar="gridToolbar"
              :padColumn="true"
              :id="'customer-pallet-account-grid'"
              :rowClick="rowClickCallBack"
              v-if="loaded"
              ref="palletAccountGrid"
              :heightPadding="20"
              :paging="false"
            />
          </div>
        </template>

        <template #rightContent>
          <gc-form-header-toolbar
            :toolbar-options="toolbarOptions"
            :data-model="model"
            :backRoute="backRoute"
            :formLocation="toolbarOptions.toolbarFormLocation"
            :itemSaveCallBack="itemSaveCallBack"
            :baseView="baseView"
            v-if="showRightContainer && loaded"
          ></gc-form-header-toolbar>

          <div class="view-card responsive-paddings">
            <GcForm
              :formOptions="formOptions"
              :formItems="formItems"
              :isNormal="true"
              :data-model="model"
              :formMode="baseView.mode"
              :valueChangeCallBack="valueChangeCallBack"
              :checkValid="isValidTrigger"
              :validationGroup="'customer-pallet-account-form'"
              v-if="showRightContainer && loaded"
            />
          </div>
        </template>
      </gc-split-view>
    </div>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import CustomerTabsMixin from "@/_mixins/customer-tabs.mixin";
import { CustomerService } from "../../_services/api/customers/customer.service";
import { CustomerDeliveryAddressService } from "../../_services/api/customers/customer-delivery-address.service";
import { CustomerPalletAccountService } from "../../_services/api/customers/customer-pallet-account.service";
import { FeatureStorage } from "../../_services/storage";

const PALLET_SIZES = ["Standard", "Quarter", "Half"];
const EXCHANGE_METHODS = ["Swap", "Transfer", "Both"];

export default {
  mixins: [RouteMixin, FormMixin, CustomerTabsMixin],
  data() {
    return {
      backButtonRoute: "/customer",
      loaded: false,
      isValidTrigger: false,
      deliveryAddresses: [],
      model: {
        RecordId: "00000000-0000-0000-0000-000000000000",
        CustomerCode: "",
        CustomerName: "",
        DeliverySiteSeqNo: 0,
        AccountName: "",
        PalletScheme: null,
        PalletSize: null,
        SchemeAccountNumber: "",
        ExchangeMethod: null,
        IsDefault: "N",
        IsDefaultBool: false,
        IsDefaultData: false,
        Active: true,
        Notes: "",
        DateChanged: "",
        DateCreated: "",
        ChangedBy: "",
        CreatedBy: "",
      },
      formOptions: {
        gcLabelLocation: "left",
      },
    };
  },
  computed: {
    backRoute() {
      return `/customer-pallet-account/${this.model.CustomerCode}`;
    },
    // A record that was already the Default when loaded can't be un-defaulted directly —
    // the only way to change the Default is to make a different account the Default instead.
    defaultCheckboxReadOnly() {
      return this.model.IsDefaultData === true;
    },
    // An account can't be set Inactive while it is (or is being made) the Default.
    activeSwitchReadOnly() {
      return this.model.IsDefaultBool === true;
    },
    toolbarOptions() {
      return {
        closeSplit: true,
        save: FeatureStorage.checkPermission("CustomerPalletAccount", "create"),
        audit: true,
        editURL: "/customer-pallet-account",
        toolbarFormItems: [
          {
            dataField: "Active",
            editorType: "dxSwitch",
            label: { text: "Active" },
            editorOptions: { readOnly: this.activeSwitchReadOnly },
          },
        ],
        toolbarFormLocation: "after",
      };
    },
    deliverySiteOptions() {
      return [{ LineNo: 0, CompanyName: "All Sites" }].concat(
        this.deliveryAddresses,
      );
    },
    dataSourceConfig() {
      return {
        allowGrouping: false,
        endpoint: "CustomerPalletAccount",
        filter: [["CustomerCode", "=", this.model.CustomerCode]],
        serverPaging: true,
      };
    },
    gridToolbar() {
      return {
        search: true,
        columnChooser: true,
        export: true,
        openFilter: true,
        expand: false,
        customButtons: [
          {
            icon: "plus",
            label: "Add New",
            buttonLocation: "left",
            visible: FeatureStorage.checkPermission("CustomerPalletAccount", "create"),
            clickAction: () => this.createNewAccount(),
          },
        ],
      };
    },
    columns() {
      return [
        {
          dataField: "DeliverySiteSeqNo",
          label: "Delivery Site",
          minWidth: 200,
          calculateCellValue: (rowData) => {
            const match = this.deliverySiteOptions.find(
              (o) => o.LineNo === rowData.DeliverySiteSeqNo,
            );
            return match ? match.CompanyName : rowData.DeliverySiteSeqNo;
          },
        },
        {
          dataField: "AccountName",
          label: "Account Name",
          width: 150,
        },
        {
          dataField: "PalletScheme",
          label: "Pallet Scheme/Provider",
          width: 150,
        },
        { dataField: "PalletSize", label: "Pallet Size", width: 110 },
        {
          dataField: "SchemeAccountNumber",
          label: "Scheme Account Number",
          width: 170,
        },
        { dataField: "ExchangeMethod", label: "Exchange Method", width: 120 },
        {
          dataField: "IsDefault",
          label: "Default",
          width: 90,
          cellTemplate: "RadioTemplate",
          makeDisabled: () => true,
        },
        { screenField: "Status", width: "auto", filterIndex: 0 },
        {
          dataField: "Notes",
          label: "Notes",
          width: 200,
          visible: false,
        },
      ];
    },
    formItems() {
      return [
        {
          itemType: "group",
          caption: "",
          colCount: 1,
          items: [
            {
              dataField: "DeliverySiteSeqNo",
              editorType: "dxSelectBox",
              required: true,
              label: { text: "Delivery Site" },
              editorOptions: {
                items: this.deliverySiteOptions,
                valueExpr: "LineNo",
                displayExpr: "CompanyName",
                searchEnabled: true,
                showClearButton: true,
              },
            },
            {
              screenField: "PalletScheme",
              dataField: "PalletScheme",
              editorType: "dxSelectBox",
              required: true,
              label: { text: "Pallet Scheme/Provider" },
              editorOptions: {
                searchEnabled: true,
                showClearButton: true,
              },
            },
            {
              dataField: "PalletSize",
              editorType: "dxSelectBox",
              required: true,
              label: { text: "Pallet Size" },
              editorOptions: {
                items: PALLET_SIZES,
                searchEnabled: true,
                showClearButton: true,
              },
            },
            {
              dataField: "AccountName",
              editorType: "dxTextBox",
              label: { text: "Account Name" },
              editorOptions: { maxLength: 255 },
            },
            {
              dataField: "SchemeAccountNumber",
              editorType: "dxTextBox",
              label: { text: "Scheme Account Number" },
              editorOptions: { maxLength: 50 },
            },
            {
              dataField: "ExchangeMethod",
              editorType: "dxSelectBox",
              label: { text: "Exchange Method" },
              editorOptions: {
                items: EXCHANGE_METHODS,
                searchEnabled: true,
                showClearButton: true,
              },
            },
            {
              dataField: "IsDefaultBool",
              editorType: "dxSwitch",
              label: { text: "Default" },
              editorOptions: {
                readOnly: this.defaultCheckboxReadOnly,
                onValueChanged: this.onDefaultValueChanged,
              },
            },
            {
              dataField: "Notes",
              editorType: "dxTextArea",
              label: { text: "Notes" },
              editorOptions: { maxLength: 4000, height: 100 },
            },
          ],
        },
      ];
    },
    showLeftContainer() {
      return !(this.isXSmall && this.baseView.mode !== "");
    },
    showRightContainer() {
      if (this.baseView.mode === "create") return true;
      if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id &&
        this.baseView.id.length === 36
      )
        return true;
      return false;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler: "handleLocalRouteChange",
    },
  },
  created() {
    this.selectedIndex = this.tabs.findIndex(
      (x) => x.text === "Pallet Accounts",
    );
  },
  methods: {
    backButtonFunction() {
      this.$router.push({
        path: "/customer",
      });
    },
    onDefaultValueChanged(e) {
      if (!e.event) return;
      if (e.value === true) this.model.Active = true;
    },
    resetModel() {
      this.model.RecordId = "00000000-0000-0000-0000-000000000000";
      this.model.DeliverySiteSeqNo = 0;
      this.model.AccountName = "";
      this.model.PalletScheme = null;
      this.model.PalletSize = null;
      this.model.SchemeAccountNumber = "";
      this.model.ExchangeMethod = null;
      this.model.IsDefault = "N";
      this.model.IsDefaultBool = false;
      this.model.IsDefaultData = false;
      this.model.Active = true;
      this.model.Notes = "";
      this.model.DateChanged = "";
      this.model.DateCreated = "";
      this.model.ChangedBy = "";
      this.model.CreatedBy = "";
    },
    createNewAccount() {
      this.resetModel();
      this.$router.push({
        path: `/customer-pallet-account/create/${this.model.CustomerCode}`,
      });
    },
    async loadCustomerContext(customerCode) {
      await CustomerService.getByCode(customerCode, ["Code", "Name"]).then(
        (res) => {
          if (res.data.value.length > 0) {
            this.model.CustomerName = res.data.value[0].Name;
          }
        },
      );

      await CustomerDeliveryAddressService.getAllByCode(customerCode).then(
        (res) => {
          this.deliveryAddresses = (res.data.value || []).map((a) => ({
            LineNo: a.LineNo,
            CompanyName: a.CompanyName,
          }));
        },
      );
    },
    async handleLocalRouteChange() {
      this.loaded = false;

      if (this.baseView.mode === "create") {
        this.resetModel();
        this.model.CustomerCode = this.baseView.id;
        await this.loadCustomerContext(this.model.CustomerCode);
        this.model.AccountName = this.model.CustomerName;
        this.baseView.tempId = this.model.CustomerCode;
        this.loaded = true;
        return;
      }

      if (
        (this.baseView.mode === "review" || this.baseView.mode === "edit") &&
        this.baseView.id &&
        this.baseView.id.length === 36
      ) {
        this.showLoading(true);
        this.model.RecordId = this.baseView.id;

        await CustomerPalletAccountService.getByRecordId(this.baseView.id).then(
          (response) => {
            if (response.data.value.length > 0) {
              const rec = response.data.value[0];
              this.model.RecordId = rec.RecordId;
              this.model.CustomerCode = rec.CustomerCode;
              this.model.DeliverySiteSeqNo = rec.DeliverySiteSeqNo;
              this.model.AccountName = rec.AccountName;
              this.model.PalletScheme = rec.PalletScheme;
              this.model.PalletSize = rec.PalletSize;
              this.model.SchemeAccountNumber = rec.SchemeAccountNumber;
              this.model.ExchangeMethod = rec.ExchangeMethod;
              this.model.IsDefault = rec.IsDefault;
              this.model.IsDefaultBool = rec.IsDefault == "Y";
              this.model.IsDefaultData = rec.IsDefault == "Y";
              this.model.Active = rec.Inactive == "N";
              this.model.Notes = rec.Notes;
              this.model.DateChanged = rec.DateChanged;
              this.model.DateCreated = rec.DateCreated;
              this.model.ChangedBy = rec.ChangedBy;
              this.model.CreatedBy = rec.CreatedBy;
            }
          },
        );

        await this.loadCustomerContext(this.model.CustomerCode);
        this.baseView.tempId = this.model.CustomerCode;
        this.loaded = true;
        this.showLoading(false);
        return;
      }

      // bare list mode — baseView.id is the customer code
      this.model.CustomerCode = this.baseView.id;
      await this.loadCustomerContext(this.model.CustomerCode);
      this.loaded = true;
    },
    rowClickCallBack(e) {
      if (!e.data) return;
      this.$router.push({
        path: `/customer-pallet-account/review/${e.data.RecordId}`,
      });
    },
    itemSaveCallBack() {
      this.isValidTrigger = true;
    },
    async valueChangeCallBack(isValid, brokenRules) {
      this.isValidTrigger = false;
      if (!isValid) {
        this.displayValidationSummary(brokenRules);
        return;
      }
      await this.saveCustomerPalletAccount();
    },
    async saveCustomerPalletAccount() {
      this.showLoading(true);

      const payload = {
        RecordId: this.baseView.mode === "create" ? null : this.model.RecordId,
        CustomerCode: this.model.CustomerCode,
        DeliverySiteSeqNo: this.model.DeliverySiteSeqNo,
        AccountName: this.model.AccountName,
        PalletScheme: this.model.PalletScheme,
        PalletSize: this.model.PalletSize,
        SchemeAccountNumber: this.model.SchemeAccountNumber,
        ExchangeMethod: this.model.ExchangeMethod,
        IsDefault: this.model.IsDefaultBool ? "Y" : "N",
        Inactive: this.model.Active ? "N" : "Y",
        Notes: this.model.Notes,
      };

      await CustomerPalletAccountService.save(payload)
        .then((res) => {
          this.showLoading(false);
          this.$notify("Customer Pallet Account saved successfully", "success");
          this.setIgnoreRouteChangeConfirmation();
          const recordId =
            res.data && res.data.RecordId
              ? res.data.RecordId
              : this.model.RecordId;
          this.baseView.tempId = this.model.CustomerCode;
          this.$router.push({
            path: `/customer-pallet-account/review/${recordId}`,
          });
        })
        .catch((error) => {
          this.showLoading(false);
          this.displayServerError(error, "Customer Pallet Account save failed");
        });
    },
  },
};
</script>
